import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PRODUCT_SEO } from '../data/seoMeta';

/* Page privée d'aperçu : comparer le rendu Google avec le prix TTC ou le prix HT.
   Non liée dans le site, non indexable (bloquée dans robots.txt). */

const SAMPLES = [
  'party-led-kit-50-personnes',
  'party-led-kit-200-personnes',
  'party-led-kit-500-personnes',
  'party-led-kit-1000-personnes',
];

const toHT = (text) =>
  text.replace(/(\d[\d\s\u00a0\u202f]*)\s*€\s*TTC/g, (_, amount) => {
    const ttc = parseInt(amount.replace(/\D/g, ''), 10);
    return `${Math.round(ttc / 1.2).toLocaleString('fr-FR')} € HT`;
  });

const Result = ({ url, title, description }) => (
  <div className="mb-8 max-w-2xl">
    <div className="flex items-center gap-2 mb-1">
      <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-[10px] flex items-center justify-center font-bold">P</span>
      <div className="leading-tight">
        <p className="text-[13px] text-[#202124]">Party LED</p>
        <p className="text-[12px] text-[#4d5156]">{url}</p>
      </div>
    </div>
    <h3 className="text-[20px] leading-[26px] text-[#1a0dab] hover:underline cursor-pointer">{title}</h3>
    <p className="text-[14px] leading-[22px] text-[#4d5156] mt-1">{description}</p>
  </div>
);

const SeoPreview = () => {
  const [mode, setMode] = useState('ttc');
  const transform = mode === 'ttc' ? (x) => x : toHT;

  return (
    <div className="min-h-screen bg-[#0b0b0c] pt-24 pb-16 px-6">
      <div className="max-w-4xl mx-auto">
        <p className="text-purple-400 text-xs tracking-[0.3em] uppercase mb-3">Aperçu privé — non publié</p>
        <h1 className="text-white font-heading text-3xl md:text-4xl font-bold mb-3">
          À quoi ressemblent vos kits dans Google
        </h1>
        <p className="text-gray-400 text-sm leading-relaxed mb-8 max-w-2xl">
          Voici exactement ce qu'un client voit dans les résultats de recherche. Basculez d'une version à l'autre
          et dites-moi celle que vous préférez : c'est le seul endroit concerné, l'affichage du site ne change pas.
        </p>

        <div className="flex gap-3 mb-10">
          <button
            onClick={() => setMode('ttc')}
            className={`px-5 py-2.5 rounded-full text-sm transition-colors ${mode === 'ttc' ? 'bg-white text-black' : 'text-gray-400 border border-white/15 hover:text-white'}`}
            data-testid="seo-preview-ttc"
          >
            Version actuelle — prix TTC
          </button>
          <button
            onClick={() => setMode('ht')}
            className={`px-5 py-2.5 rounded-full text-sm transition-colors ${mode === 'ht' ? 'bg-white text-black' : 'text-gray-400 border border-white/15 hover:text-white'}`}
            data-testid="seo-preview-ht"
          >
            Autre version — prix HT
          </button>
        </div>

        <div className="bg-white rounded-2xl p-8" data-testid="seo-preview-results">
          <p className="text-[13px] text-[#70757a] mb-6">Environ 11 résultats pour « kit bracelets LED »</p>
          {SAMPLES.map((slug) => {
            const seo = PRODUCT_SEO[slug];
            if (!seo) return null;
            return (
              <Result
                key={slug}
                url={`www.party-led.com › produit › ${slug.replace('party-led-', '')}`}
                title={transform(seo.title)}
                description={transform(seo.description)}
              />
            );
          })}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-2 text-base">Si on garde le TTC</h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              Le client voit le montant qu'il paiera vraiment, il n'y a pas de mauvaise surprise au panier.
              C'est ce que Google attend pour un public de particuliers — mariages, anniversaires, soirées.
              Le prix paraît plus élevé au premier regard.
            </p>
          </div>
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-2 text-base">Si on passe au HT</h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              Le prix affiché est plus bas, donc plus attractif face à un concurrent, et c'est la référence
              naturelle pour les entreprises, festivals et clubs qui récupèrent la TVA. Un particulier peut en
              revanche se sentir trompé en découvrant 20 % de plus au moment de payer.
            </p>
          </div>
        </div>

        <div className="mt-10">
          <Link to="/" className="text-purple-400 text-sm hover:text-purple-300">← Retour au site</Link>
        </div>
      </div>
    </div>
  );
};

export default SeoPreview;
